import { Autocomplete, Button, Checkbox, Divider, Group, Radio, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { zodResolver } from "mantine-form-zod-resolver";
import { IMaskInput } from "react-imask";
import { z } from "zod";
import { IEvent } from "../../../models/events";
import { useEventStore } from "../../../store/events.store";
import { useDrawerStore } from "../../../store/dashboard.store";
import { useEffect, useState } from "react";
import { useDebouncedState } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";

const AddPatient = ({ event } : {event: IEvent}) => {
    const AddPatient = useEventStore((state) => state.addPatient);
    const popPage = useDrawerStore((state) => state.popPage);
    const searchPatients = useEventStore((state) => state.searchPatients);
    const getPatient = useEventStore((state) => state.getPatient);
    const [loading, setLoading] = useState(false);
    const [textSearchValue, setTextSearchValue] = useDebouncedState('', 500);
    const patientsFromSearch = useEventStore((state) => state.patientsFromSearch);
    const [selectedPatient, setSelectedPatient] = useState<string | null>(null);
    

    const generalScheme = {
        firstName: z.string().min(2, { message: 'First name is too short' }),
        lastName: z.string().min(2, { message: 'Last name is too short' }),
        phone: z.string().min(10, { message: 'Phone number is too short' }),
        email: z.string().nullable(),
        dob: z.date(),
        homeAddress: z.string().nullable(),
        city: z.string().nullable(),
        zipCode: z.string().nullable(),
        officeStaffOnly: z.boolean(),
        isNewPatient: z.enum(['yes', 'no']),
    };
    

    const scheme = z.object(generalScheme);

    const form = useForm({
       mode: 'uncontrolled',
       validate: zodResolver(scheme),
       initialValues: {
            firstName: '',
            lastName: '',
            phone: '',
            email: '',
            dob: '',
            homeAddress: '',
            city: '',
            zipCode: '',
            officeStaffOnly: false,
            isNewPatient: 'no',
       }
    });


    useEffect(() => {
        const fetchPatients = (async () => {

            if (textSearchValue)
                searchPatients(textSearchValue);
        });
        
        fetchPatients();

    }, [searchPatients, textSearchValue]);

    return (
        <>
            <div>
                <h3>Search prevous patient</h3>
                <Autocomplete
                  placeholder="Search Patient"
                  label="Search prevous patient(s)"
                  data={patientsFromSearch.map((patient) => ({
                    value: patient.uid!,
                    label: `${patient.firstName} ${patient.lastName}`,
                  }))}
                  onChange={(value: string) => {
                    setTextSearchValue(value);
                  }}
                  onOptionSubmit={(value) => {
                    setSelectedPatient(value);
                  }}
                />
                {
                    selectedPatient && <Button mt="md" 
                    variant="outline"
                    loading={loading}
                    onClick={async () => {
                        setLoading(true);
                        const patient = await getPatient(selectedPatient); 
                        if (!patient) {
                            setLoading(false);
                            notifications.show(
                                {title: 'Error', message: 'Patient not found', color: 'red', icon: 'cross1'}
                            );
                            return;
                        }
                        const check = await AddPatient(event, patient!);
                        setLoading(false);
                        if (check)
                            popPage();
                    }}>Add Patient</Button>
                }
            </div>
            <br/>
            <Divider />
            <br/>
            <form onSubmit={form.onSubmit(async (values) => {
                setLoading(true);
                const check = await AddPatient(event, {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    phone: values.phone,
                    email: values.email,
                    dob: new Date(values.dob).toISOString(),
                    address: values.homeAddress,
                    city: values.city,
                    zipCode: values.zipCode,
                    officeStaffOnly: values.officeStaffOnly,
                    isNewPatient: values.isNewPatient,
                    latestEventId: event.uid,
                    events: [event.uid!],
                });
                setLoading(false);
                if (check)
                    popPage();
            })}>
                <h3>Add Patient Form</h3>
                <Group grow>
                    <TextInput {...form.getInputProps('firstName')} label="First Name" placeholder="Enter your first name" mb="md" />
                    <TextInput {...form.getInputProps('lastName')} label="Last Name" placeholder="Enter your last name" mb="md" />
                </Group>
                
                <TextInput {...form.getInputProps('phone')}
                    component={IMaskInput}
                    // @ts-expect-error - IMaskInput props are not typed
                    mask="(000) 000-0000"
                    type="tel"
                    label="Phone" placeholder="Enter your phone number" mb="md" />
                <TextInput {...form.getInputProps('email')} label="Email" placeholder="Enter your email" mb="md" />
                <h4>Date of Birth</h4>
                <Group grow>
                    <DateInput
                        label="Date of Birth"
                        placeholder="Select date of birth"
                        required
                        {...form.getInputProps('dob')}
                    />
                </Group>
                    <h4>Home Address</h4>
                    <TextInput {...form.getInputProps('homeAddress')} label="Address" placeholder="Enter your address" mb="md" />
                    <Group grow >
                        <TextInput {...form.getInputProps('city')} label="City" placeholder="Enter your city" mb="md" />
                        <TextInput {...form.getInputProps('zipCode')} label="Zip Code" placeholder="Enter your zip code" mb="md" />
                    </Group>
                <>
                    <h4>Additional Information</h4>
                    <Checkbox 
                        {...form.getInputProps('officeStaffOnly', { type: 'checkbox'})} 
                        label="Please check this box if you need to ask a question to office staff and no doctor visit needed." mb="md" 
                        key={form.key('officeStaffOnly')} />
                    <Radio.Group  
                        label="Are you a new patient?"
                        description="Please select if you are a new patient or not"
                        withAsterisk
                        name="isNewPatient"
                        {...form.getInputProps('isNewPatient')}>
                        <Radio value="no" label="No, I am not a new patient" mb="md" />
                        <Radio value="yes" label="Yes, I am a new patient" mb="md" />
                    </Radio.Group>
                </>
                <br />
                <Button 
                    loading={loading} 
                    disabled={loading} 
                    variant="outline" 
                    color="blue"
                    type="submit">
                    Add Patient
                </Button>
            </form>
        </>
    );
};

export default AddPatient;